import { transformGenerality } from 'front-office/utils/release-id-card-generality-transformer';
import { transformCitizenship } from 'front-office/utils/release-id-card-citizenship-transformer';
import { transformResidence } from 'front-office/utils/release-id-card-residence-transformer';
import { transformPerson } from 'front-office/utils/release-id-card-person-transformer';
import { transformDocument } from 'front-office/utils/release-id-card-document-transformer';
import { AxiosResponse } from 'axios';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseAnprData } from 'shell/models/person';
import { ReleaseIdCard } from 'front-office/view-models/release-id-card';
import { DateTime } from 'luxon';
import { UserProfile } from 'shell/hooks/use-user-profile/types';
import { AnprVM } from 'shell/view-models/search-anpr';
import { PersonVM } from 'shell/view-models/search-person';
import { AccordionFamilyCoexistence } from 'shell/view-models/accordion-family-coexistence';

export const releaseIdCardResponseTransformer = (
  response: AxiosResponse<HttpResponse<ResponseAnprData>>,
  userProfile: UserProfile
): ReleaseIdCard => {
  const responseToTransform = response.data.payload?.risposta3002OK || {};

  const subjectData = responseToTransform.listaSoggetti?.datiSoggetto[0] || {};

  const headingResponse = responseToTransform.testataRisposta;

  const isItalian = !!subjectData.cittadinanza?.find((c) => c.codiceStato === '100');

  const person = transformPerson(subjectData);

  const matrimonio = { ...subjectData.matrimonio };
  const statoCivile = { ...subjectData.statoCivile };

  const residence = transformResidence(subjectData);

  const isResident =
    subjectData?.datiIscrizione?.datiIscrizioneComune?.comune?.codiceIstat === '015146' ||
    subjectData?.datiIscrizione?.datiIscrizioneComune?.comune?.nomeComune?.toLowerCase() ===
      'milano';

  const family: AccordionFamilyCoexistence = {
    relationship: subjectData.legameSoggetto?.codiceLegame,
    descrizioneCodiceLegame: subjectData.legameSoggetto?.descrizioneCodiceLegame,
  };

  return {
    isDocument: true,
    isList: responseToTransform.listaSoggetti?.datiSoggetto?.length > 1,
    anomalie: responseToTransform.noteAnomalie,
    dati3002: subjectData,
    isItalian,
    family,
    isResident,
    person,
    controlData: {
      effectiveDate: headingResponse?.dataDecorrenza
        ? DateTime.fromISO(headingResponse.dataDecorrenza)
        : null,
      validationFiscalCode: '0',
    },
    registryOffice: {
      generality: transformGenerality(subjectData),
      isCitizenshipEditable: !(subjectData.cittadinanza && subjectData.cittadinanza.length > 0),
      citizenship: transformCitizenship(subjectData),
    },
    residence,
    document: {
      ...transformDocument(subjectData),
      printCivilStatus: '',
      municipality: {
        provinceCode: userProfile.datiLogin?.siglaProvincia,
        description: userProfile.datiLogin?.nomeComune || '',
        province: userProfile.datiLogin?.siglaProvincia || '',
      },
    },
    matrimonio,
    statoCivile,
    attoNascita: subjectData?.attoNascita,
    datiIscrizione: subjectData?.datiIscrizione,
    maternita: subjectData?.maternita,
    paternita: subjectData?.paternita,
  };
};

export interface ReleaseIdCardTransofrmSearchType {
  releaseIdCard: ReleaseIdCard;
  anprPersonData: AnprVM<PersonVM>;
}

export const releaseIdCardResponseTransformerForSearch = (
  response: AxiosResponse<HttpResponse<ResponseAnprData>>,
  userProfile: UserProfile
): ReleaseIdCardTransofrmSearchType => {
  const responseToTransform = response.data.payload?.risposta3002OK || {};

  const subjectData = responseToTransform.listaSoggetti?.datiSoggetto[0] || {};

  const headingResponse = responseToTransform.testataRisposta;

  const isItalian = !!subjectData.cittadinanza?.find((c) => c.codiceStato === '100');

  const person = transformPerson(subjectData);

  const matrimonio = { ...subjectData.matrimonio };
  const statoCivile = { ...subjectData.statoCivile };

  return {
    releaseIdCard: {
      isDocument: true,
      isList: responseToTransform.listaSoggetti?.datiSoggetto?.length > 1,
      onlyOneElement: responseToTransform.listaSoggetti?.datiSoggetto?.length === 1,
      dati3002: subjectData,
      isItalian,
      person,
      controlData: {
        effectiveDate: headingResponse?.dataDecorrenza
          ? DateTime.fromISO(headingResponse.dataDecorrenza)
          : null,
        validationFiscalCode: '0',
      },
      registryOffice: {
        generality: transformGenerality(subjectData),
        isCitizenshipEditable: !(subjectData.cittadinanza && subjectData.cittadinanza.length > 0),
        citizenship: transformCitizenship(subjectData),
      },
      residence: transformResidence(subjectData),
      document: {
        ...transformDocument(subjectData),
        municipality: {
          provinceCode: userProfile.datiLogin?.siglaProvincia,
          description: userProfile.datiLogin?.nomeComune || '',
          province: userProfile.datiLogin?.siglaProvincia || '',
        },
      },
      matrimonio,
      statoCivile,
      attoNascita: subjectData?.attoNascita,
      datiIscrizione: subjectData?.datiIscrizione,
      maternita: subjectData?.maternita,
      paternita: subjectData?.paternita,
    },
    anprPersonData: {
      warnings: (response.data.payload?.risposta3002OK?.listaAnomalie?.errore || []).map(
        (warning) => ({
          id: warning.codiceErroreAnomalia,
          value: warning.testoErroreAnomalia,
        })
      ),
      errors: (response.data.payload?.rispostaKO?.listaErrori?.errore || []).map((error) => ({
        id: error.codiceErroreAnomalia,
        value: error.testoErroreAnomalia,
      })),
      persons: (response.data.payload?.risposta3002OK?.listaSoggetti?.datiSoggetto || []).map(
        (singlePerson, index) => {
          const residenceArray = singlePerson.residenza || [];

          let birthplace = '';
          // birthplace check for object
          if (singlePerson.generalita?.luogoNascita?.localita) {
            birthplace = `${singlePerson.generalita?.luogoNascita?.localita.descrizioneLocalita} (${singlePerson.generalita?.luogoNascita?.localita.descrizioneStato})`;
          }

          if (singlePerson.generalita?.luogoNascita?.comune) {
            birthplace = `${singlePerson.generalita?.luogoNascita?.comune.nomeComune} (${singlePerson.generalita?.luogoNascita?.comune.siglaProvinciaIstat})`;
          }
          // Residence check for object
          let residence = '';
          // scelgo il primo o è un array?
          if (residenceArray[0]?.indirizzo) {
            residence = `${residenceArray[0]?.indirizzo.comune?.nomeComune || ''} (${
              residenceArray[0]?.indirizzo.comune?.siglaProvinciaIstat || ''
            })`;
          }
          // scelgo il primo o è un array?
          if (residenceArray[0]?.localitaEstera) {
            residence = `${residenceArray[0]?.localitaEstera.indirizzoEstero?.localita?.descrizioneLocalita} (${residenceArray[0]?.localitaEstera.indirizzoEstero?.localita?.descrizioneStato})`;
          }

          let state = '';
          // state check for object
          if (residenceArray[0]?.indirizzo?.toponimo) {
            const toponym = residenceArray[0]?.indirizzo?.toponimo;

            const civic = residenceArray[0]?.indirizzo?.numeroCivico;

            state = `${toponym?.specie || ''} ${toponym?.denominazioneToponimo || ''},${
              civic?.numero || ''
            }${civic?.esponente1 ? `/${civic.esponente1 || ''}${civic.lettera || ''}` : ''}`;
          }

          if (
            singlePerson.generalita?.soggettoAIRE &&
            singlePerson.generalita?.soggettoAIRE === 'S'
          ) {
            state = 'Iscritto AIRE';
          }

          if (
            singlePerson.datiCancellazione &&
            singlePerson.datiCancellazione?.descrizioneMotivoCancellazione
          ) {
            state = `${singlePerson.datiCancellazione.descrizioneMotivoCancellazione}`;
          }

          let statePerson = '';
          // state check for object
          if (residenceArray[0]?.indirizzo?.toponimo) {
            statePerson = 'Residente';
          }
          if (
            singlePerson.generalita?.soggettoAIRE &&
            singlePerson.generalita?.soggettoAIRE === 'S'
          ) {
            statePerson = 'Iscritto AIRE';
          }
          if (
            singlePerson.datiCancellazione &&
            singlePerson.datiCancellazione?.descrizioneMotivoCancellazione
          ) {
            statePerson = `${singlePerson.datiCancellazione.descrizioneMotivoCancellazione}`;
          }

          return {
            dati3002: response.data.payload,
            generality: singlePerson.generalita,
            citizenship: singlePerson.cittadinanza || undefined,
            residenceAll: singlePerson.residenza || undefined,
            residence,
            statePerson,
            code: singlePerson.generalita?.codiceFiscale?.codFiscale || (index + 1).toString(),
            fiscalCode: singlePerson.generalita?.codiceFiscale?.codFiscale || '',
            surname: singlePerson.generalita?.cognome || '',
            name: singlePerson.generalita?.nome || '',
            gender: singlePerson.generalita?.sesso || '',
            birthday: singlePerson.generalita?.dataNascita
              ? DateTime.fromISO(singlePerson.generalita.dataNascita).toFormat('dd/MM/yyyy')
              : '',
            birthplace,
            state,
            anpr: singlePerson.generalita?.idSchedaSoggettoANPR || '',
            idFamilyAnpr: singlePerson.famigliaConvivenza?.idFamigliaConvivenzaANPR || '',
            hidden: {
              delete: true,
              edit: true,
            },
          };
        }
      ) as Array<PersonVM>,
    },
  };
};
